import Vue from 'vue'
import VueRouter from 'vue-router'
import { applySeo } from '@/utils/seo'

if (typeof window !== 'undefined') {
  window.history.scrollRestoration = 'manual'
}

Vue.use(VueRouter)

const getMobileHomePath = () => '/m'

/** 路由 SEO meta：title 为菜单短标题；标签显示「金税云大数据—菜单名」 */
function seo(title, description, layout = 'pc', extra = {}) {
  return {
    title,
    description,
    keywords: extra.keywords,
    isHome: !!extra.isHome,
    layout,
    ...extra
  }
}

const HOME_DESC =
  '金税云（山东）大数据服务有限公司（山东金税云）是国内领先的财税数智化综合解决方案提供商，提供业票财税银档一体化服务，助力企业合规、降本、增效、创利。'
const ABOUT_DESC =
  '了解金税云（山东）大数据服务有限公司（山东金税云）的发展历程、企业使命与核心优势，深耕企业财税数字化服务。'
const PRODUCTS_DESC =
  '山东金税云业务介绍：销项/进项发票、纳税申报、税务风控、电子档案、AI智能体等财税数字化产品与解决方案。'
const CASES_DESC =
  '山东金税云客户案例：覆盖基建、能源、制造、金融、零售等多行业数字化税务解决方案与实践。'
const CAREERS_DESC =
  '加入金税云（山东）大数据服务有限公司（山东金税云），与专业团队一起推动企业财税管理迈入数智化新时代。'

function productDesc(name) {
  return `${name}是金税云（山东）大数据服务有限公司（山东金税云）推出的企业财税数字化产品，助力合规提效。`
}

function caseDesc(industry) {
  return `${industry}数字化税务解决方案 - 金税云（山东）大数据服务有限公司（山东金税云）行业实践与客户案例。`
}

const routes = [
  {
    path: '/',
    component: () => import('@/views/layout/index.vue'),
    beforeEnter(to, from, next) {
      // 小屏访问根路径时进入移动端首页
      if (
        to.path === '/' &&
        typeof window !== 'undefined' &&
        window.innerWidth < 768
      ) {
        next(getMobileHomePath())
        return
      }
      next()
    },
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import('@/views/home/index.vue'),
        meta: seo('首页', HOME_DESC, 'pc', { isHome: true })
      },
      {
        path: 'about',
        name: 'About',
        component: () => import('@/views/about/index.vue'),
        meta: seo('关于我们', ABOUT_DESC)
      },
      {
        path: 'products',
        name: 'Products',
        component: () => import('@/views/products/index.vue'),
        meta: seo('业务介绍', PRODUCTS_DESC)
      },
      {
        path: 'products/output-invoice',
        name: 'OutputInvoice',
        component: () => import('@/views/products/output-invoice.vue'),
        meta: seo('销项发票管理系统', productDesc('销项发票管理系统'))
      },
      {
        path: 'products/input-invoice',
        name: 'InputInvoice',
        component: () => import('@/views/products/input-invoice.vue'),
        meta: seo('进项发票管理系统', productDesc('进项发票管理系统'))
      },
      {
        path: 'products/electronic-archive',
        name: 'ElectronicArchive',
        component: () => import('@/views/products/electronic-archive.vue'),
        meta: seo('电子会计档案管理系统', productDesc('电子会计档案管理系统'))
      },
      {
        path: 'products/tax-declaration',
        name: 'TaxDeclaration',
        component: () => import('@/views/products/tax-declaration.vue'),
        meta: seo('纳税申报管理系统', productDesc('纳税申报管理系统'))
      },
      {
        path: 'products/one-click-declaration',
        name: 'OneClickDeclaration',
        component: () => import('@/views/products/one-click-declaration.vue'),
        meta: seo('智能一键申报平台', productDesc('智能一键申报平台'))
      },
      {
        path: 'products/tax-risk-management',
        name: 'TaxRiskManagement',
        component: () => import('@/views/products/tax-risk-management.vue'),
        meta: seo('税务风险管理系统', productDesc('税务风险管理系统'))
      },
      {
        path: 'products/supply-chain',
        name: 'SupplyChain',
        component: () => import('@/views/products/supply-chain.vue'),
        meta: seo('供应链协同系统', productDesc('供应链协同系统'))
      },
      {
        path: 'products/smart-city',
        name: 'SmartCity',
        component: () => import('@/views/products/smart-city.vue'),
        meta: seo('智慧城市解决方案', productDesc('智慧城市解决方案'))
      },
      {
        path: 'products/ai-agent',
        name: 'AiAgent',
        component: () => import('@/views/products/ai-agent.vue'),
        meta: seo('AI智能体财税协同', productDesc('AI智能体财税协同'))
      },
      {
        path: 'products/tax-model',
        name: 'TaxModel',
        component: () => import('@/views/products/tax-model.vue'),
        meta: seo('财税大模型平台', productDesc('财税大模型平台'))
      },
      {
        path: 'cases',
        name: 'Cases',
        component: () => import('@/views/cases/index.vue'),
        meta: seo('客户案例', CASES_DESC)
      },
      {
        path: 'careers',
        name: 'Careers',
        component: () => import('@/views/careers/index.vue'),
        meta: seo('加入我们', CAREERS_DESC)
      },
      {
        path: 'cases/infrastructure',
        name: 'InfrastructureIndustry',
        component: () => import('@/views/cases/infrastructure.vue'),
        meta: seo('基建行业', caseDesc('基建行业'))
      },
      {
        path: 'cases/energy',
        name: 'EnergyIndustry',
        component: () => import('@/views/cases/energy.vue'),
        meta: seo('能源行业', caseDesc('能源行业'))
      },
      {
        path: 'cases/medicine',
        name: 'MedicineIndustry',
        component: () => import('@/views/cases/medicine.vue'),
        meta: seo('医药行业', caseDesc('医药行业'))
      },
      {
        path: 'cases/manufacturing',
        name: 'ManufacturingIndustry',
        component: () => import('@/views/cases/manufacturing.vue'),
        meta: seo('制造行业', caseDesc('制造行业'))
      },
      {
        path: 'cases/finance',
        name: 'FinanceIndustry',
        component: () => import('@/views/cases/finance.vue'),
        meta: seo('银行保险行业', caseDesc('银行保险行业'))
      },
      {
        path: 'cases/food',
        name: 'FoodIndustry',
        component: () => import('@/views/cases/food.vue'),
        meta: seo('餐饮行业', caseDesc('餐饮行业'))
      },
      {
        path: 'cases/car',
        name: 'CarIndustry',
        component: () => import('@/views/cases/car.vue'),
        meta: seo('汽车行业', caseDesc('汽车行业'))
      },
      {
        path: 'cases/property',
        name: 'PropertyIndustry',
        component: () => import('@/views/cases/property.vue'),
        meta: seo('园区物业行业', caseDesc('园区物业行业'))
      },
      {
        path: 'cases/e-commerce',
        name: 'ECommerceIndustry',
        component: () => import('@/views/cases/e-commerce.vue'),
        meta: seo('电商行业', caseDesc('电商行业'))
      },
      {
        path: 'cases/real-estate',
        name: 'RealEstateIndustry',
        component: () => import('@/views/cases/real-estate.vue'),
        meta: seo('地产行业', caseDesc('地产行业'))
      },
      {
        path: 'cases/retail',
        name: 'RetailIndustry',
        component: () => import('@/views/cases/retail.vue'),
        meta: seo('零售商超行业', caseDesc('零售商超行业'))
      },
      {
        path: 'cases/steel',
        name: 'SteelIndustry',
        component: () => import('@/views/cases/steel.vue'),
        meta: seo('钢铁行业', caseDesc('钢铁行业'))
      },
      {
        path: 'cases/wine',
        name: 'WineIndustry',
        component: () => import('@/views/cases/wine.vue'),
        meta: seo('酒品行业', caseDesc('酒品行业'))
      },
      {
        path: 'cases/tourism',
        name: 'TourismIndustry',
        component: () => import('@/views/cases/tourism.vue'),
        meta: seo('文旅行业', caseDesc('文旅行业'))
      },
      {
        path: 'article/:id',
        name: 'ArticleDetail',
        component: () => import('@/views/article/index.vue'),
        meta: seo('文章详情', '山东金税云财税数字化资讯与文章详情。')
      }
    ]
  },
  {
    path: '/home',
    redirect: '/'
  },
  {
    path: '/m',
    component: () => import('@/views/m-layout/index.vue'),
    children: [
      {
        path: '',
        name: 'MobileHome',
        component: () => import('@/views/m-home/index.vue'),
        meta: seo('首页', HOME_DESC, 'mobile', { isHome: true })
      },
      {
        path: 'about',
        name: 'MobileAbout',
        component: () => import('@/views/m-about/index.vue'),
        meta: seo('关于我们', ABOUT_DESC, 'mobile')
      },
      {
        path: 'products',
        name: 'MobileProducts',
        component: () => import('@/views/m-products/index.vue'),
        meta: seo('业务介绍', PRODUCTS_DESC, 'mobile')
      },
      {
        path: 'products/output-invoice',
        name: 'MobileOutputInvoice',
        component: () => import('@/views/m-products/m-output-invoice.vue'),
        meta: seo('销项发票管理系统', productDesc('销项发票管理系统'), 'mobile')
      },
      {
        path: 'products/input-invoice',
        name: 'MobileInputInvoice',
        component: () => import('@/views/m-products/m-input-invoice.vue'),
        meta: seo('进项发票管理系统', productDesc('进项发票管理系统'), 'mobile')
      },
      {
        path: 'products/electronic-archive',
        name: 'MobileElectronicArchive',
        component: () => import('@/views/m-products/m-electronic-archive.vue'),
        meta: seo('电子会计档案管理系统', productDesc('电子会计档案管理系统'), 'mobile')
      },
      {
        path: 'products/tax-declaration',
        name: 'MobileTaxDeclaration',
        component: () => import('@/views/m-products/m-tax-declaration.vue'),
        meta: seo('纳税申报管理系统', productDesc('纳税申报管理系统'), 'mobile')
      },
      {
        path: 'products/one-click-declaration',
        name: 'MobileOneClickDeclaration',
        component: () => import('@/views/m-products/m-one-click-declaration.vue'),
        meta: seo('智能一键申报平台', productDesc('智能一键申报平台'), 'mobile')
      },
      {
        path: 'products/tax-risk-management',
        name: 'MobileTaxRiskManagement',
        component: () => import('@/views/m-products/m-tax-risk-management.vue'),
        meta: seo('税务风险管理系统', productDesc('税务风险管理系统'), 'mobile')
      },
      {
        path: 'products/supply-chain',
        name: 'MobileSupplyChain',
        component: () => import('@/views/m-products/m-supply-chain.vue'),
        meta: seo('供应链协同系统', productDesc('供应链协同系统'), 'mobile')
      },
      {
        path: 'products/smart-city',
        name: 'MobileSmartCity',
        component: () => import('@/views/m-products/m-smart-city.vue'),
        meta: seo('智慧城市解决方案', productDesc('智慧城市解决方案'), 'mobile')
      },
      {
        path: 'products/ai-agent',
        name: 'MobileAiAgent',
        component: () => import('@/views/m-products/m-ai-agent.vue'),
        meta: seo('AI智能体财税协同', productDesc('AI智能体财税协同'), 'mobile')
      },
      {
        path: 'products/tax-model',
        name: 'MobileTaxModel',
        component: () => import('@/views/m-products/m-tax-model.vue'),
        meta: seo('财税大模型平台', productDesc('财税大模型平台'), 'mobile')
      },
      {
        path: 'cases',
        name: 'MobileCases',
        component: () => import('@/views/m-cases/index.vue'),
        meta: seo('客户案例', CASES_DESC, 'mobile')
      },
      {
        path: 'cases/infrastructure',
        name: 'MobileInfrastructureIndustry',
        component: () => import('@/views/m-cases/m-infrastructure.vue'),
        meta: seo('基建行业', caseDesc('基建行业'), 'mobile')
      },
      {
        path: 'cases/energy',
        name: 'MobileEnergyIndustry',
        component: () => import('@/views/m-cases/m-energy.vue'),
        meta: seo('能源行业', caseDesc('能源行业'), 'mobile')
      },
      {
        path: 'cases/medicine',
        name: 'MobileMedicineIndustry',
        component: () => import('@/views/m-cases/m-medicine.vue'),
        meta: seo('医药行业', caseDesc('医药行业'), 'mobile')
      },
      {
        path: 'cases/manufacturing',
        name: 'MobileManufacturingIndustry',
        component: () => import('@/views/m-cases/m-manufacturing.vue'),
        meta: seo('制造行业', caseDesc('制造行业'), 'mobile')
      },
      {
        path: 'cases/finance',
        name: 'MobileFinanceIndustry',
        component: () => import('@/views/m-cases/m-finance.vue'),
        meta: seo('银行保险行业', caseDesc('银行保险行业'), 'mobile')
      },
      {
        path: 'cases/food',
        name: 'MobileFoodIndustry',
        component: () => import('@/views/m-cases/m-food.vue'),
        meta: seo('餐饮行业', caseDesc('餐饮行业'), 'mobile')
      },
      {
        path: 'cases/car',
        name: 'MobileCarIndustry',
        component: () => import('@/views/m-cases/m-car.vue'),
        meta: seo('汽车行业', caseDesc('汽车行业'), 'mobile')
      },
      {
        path: 'cases/property',
        name: 'MobilePropertyIndustry',
        component: () => import('@/views/m-cases/m-property.vue'),
        meta: seo('园区物业行业', caseDesc('园区物业行业'), 'mobile')
      },
      {
        path: 'cases/e-commerce',
        name: 'MobileECommerceIndustry',
        component: () => import('@/views/m-cases/m-e-commerce.vue'),
        meta: seo('电商行业', caseDesc('电商行业'), 'mobile')
      },
      {
        path: 'cases/real-estate',
        name: 'MobileRealEstateIndustry',
        component: () => import('@/views/m-cases/m-real-estate.vue'),
        meta: seo('地产行业', caseDesc('地产行业'), 'mobile')
      },
      {
        path: 'cases/retail',
        name: 'MobileRetailIndustry',
        component: () => import('@/views/m-cases/m-retail.vue'),
        meta: seo('零售商超行业', caseDesc('零售商超行业'), 'mobile')
      },
      {
        path: 'cases/steel',
        name: 'MobileSteelIndustry',
        component: () => import('@/views/m-cases/m-steel.vue'),
        meta: seo('钢铁行业', caseDesc('钢铁行业'), 'mobile')
      },
      {
        path: 'cases/wine',
        name: 'MobileWineIndustry',
        component: () => import('@/views/m-cases/m-wine.vue'),
        meta: seo('酒品行业', caseDesc('酒品行业'), 'mobile')
      },
      {
        path: 'cases/tourism',
        name: 'MobileTourismIndustry',
        component: () => import('@/views/m-cases/m-tourism.vue'),
        meta: seo('文旅行业', caseDesc('文旅行业'), 'mobile')
      },
      {
        path: 'careers',
        name: 'MobileCareers',
        component: () => import('@/views/m-careers/index.vue'),
        meta: seo('加入我们', CAREERS_DESC, 'mobile')
      }
    ]
  },
  {
    path: '/m/home',
    redirect: '/m'
  },
  {
    path: '/m/',
    redirect: '/m'
  }
]

if (typeof window !== 'undefined') {
  window.history.scrollRestoration = 'manual'
}

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }

    if (to.hash) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ selector: to.hash, behavior: 'smooth' })
        }, 100)
      })
    }

    return { x: 0, y: 0 }
  }
})

router.beforeEach((to, from, next) => {
  applySeo(to.meta || {}, to.path)
  if (!to.hash) {
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }
  next()
})

router.afterEach((to) => {
  if (!to.hash) {
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
    requestAnimationFrame(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      setTimeout(() => {
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      }, 300)
    })
  }
})

export default router
